import { Action } from './action';

export const ALIGN_PAGE_TYPES = {
  top: 'top',
  left: 'left',
  middle: 'middle',
  center: 'center',
  bottom: 'bottom',
  right: 'right',
};

// Helper functions for advanced alignment features
function calculateBounds(rects) {
  const minX = Math.min(...rects.map((r) => r.x));
  const minY = Math.min(...rects.map((r) => r.y));
  const maxX = Math.max(...rects.map((r) => r.x + r.w));
  const maxY = Math.max(...rects.map((r) => r.y + r.h));

  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function calculateGaps(rects, direction) {
  const sorted = [...rects].sort((a, b) => (direction === 'horizontal' ? a.x - b.x : a.y - b.y));

  const gaps = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i];
    const next = sorted[i + 1];

    if (direction === 'horizontal') {
      gaps.push(next.x - (current.x + current.w));
    } else {
      gaps.push(next.y - (current.y + current.h));
    }
  }

  return gaps;
}

function applyAlignmentUpdates(stageContext, updates) {
  if (!updates || !updates.updates) return false;

  let hasChanges = false;

  updates.updates.forEach((update) => {
    const node = stageContext.api.getNode(update.nodeId);
    if (node && update.transform) {
      stageContext.api.updateNode(update.nodeId, {
        x: update.transform.x,
        y: update.transform.y,
      });
      hasChanges = true;
    }
  });

  return hasChanges;
}

function performDistribution(stageContext, nodes, direction) {
  const sorted = [...nodes].sort((a, b) => {
    const rectA = stageContext.api.getNodeRegion(a).rect;
    const rectB = stageContext.api.getNodeRegion(b).rect;
    return direction === 'horizontal' ? rectA.x - rectB.x : rectA.y - rectB.y;
  });

  const sortedRects = sorted.map((node) => stageContext.api.getNodeRegion(node).rect);
  const bounds = calculateBounds(sortedRects);

  const totalNodeSize = sortedRects.reduce(
    (sum, rect) => sum + (direction === 'horizontal' ? rect.w : rect.h),
    0
  );
  const availableSpace = (direction === 'horizontal' ? bounds.w : bounds.h) - totalNodeSize;
  const gap = availableSpace / (sorted.length - 1);

  const updates = [];
  let currentPosition = direction === 'horizontal' ? bounds.x : bounds.y;

  sorted.forEach((node, index) => {
    const rect = sortedRects[index];

    if (direction === 'horizontal') {
      updates.push({
        nodeId: node.id,
        transform: { x: currentPosition, y: rect.y },
      });
      currentPosition += rect.w + gap;
    } else {
      updates.push({
        nodeId: node.id,
        transform: { x: rect.x, y: currentPosition },
      });
      currentPosition += rect.h + gap;
    }
  });

  return { updates, type: `distribute_${direction}` };
}

function performSpacing(stageContext, nodes, direction) {
  const rects = nodes.map((node) => stageContext.api.getNodeRegion(node).rect);
  const gaps = calculateGaps(rects, direction);

  if (gaps.length === 0) return null;

  const averageGap = gaps.reduce((sum, gap) => sum + gap, 0) / gaps.length;

  // Apply average spacing
  const sorted = [...nodes].sort((a, b) => {
    const rectA = stageContext.api.getNodeRegion(a).rect;
    const rectB = stageContext.api.getNodeRegion(b).rect;
    return direction === 'horizontal' ? rectA.x - rectB.x : rectA.y - rectB.y;
  });

  const updates = [];
  let currentPosition =
    direction === 'horizontal'
      ? stageContext.api.getNodeRegion(sorted[0]).rect.x
      : stageContext.api.getNodeRegion(sorted[0]).rect.y;

  sorted.forEach((node, index) => {
    const rect = stageContext.api.getNodeRegion(node).rect;

    if (index > 0) {
      if (direction === 'horizontal') {
        updates.push({
          nodeId: node.id,
          transform: { x: currentPosition, y: rect.y },
        });
        currentPosition += rect.w + averageGap;
      } else {
        updates.push({
          nodeId: node.id,
          transform: { x: rect.x, y: currentPosition },
        });
        currentPosition += rect.h + averageGap;
      }
    } else {
      currentPosition += direction === 'horizontal' ? rect.w + averageGap : rect.h + averageGap;
    }
  });

  return { updates, type: `space_${direction}` };
}

export const createAlignHandler = (type) => (stageContext) => {
  const selectedNodes = stageContext.api.getSelectedNodes();
  if (!selectedNodes.length) return;
  const node = selectedNodes[0];
  const id = node.id;
  alignPageNode(stageContext, id, type);
};

export const createGroupLayerAlignHandler = (type) => (stageContext) => {
  const selectedNodes = stageContext.api.getSelectedNodes();
  if (selectedNodes.length < 2) return;
  alignNodeGroup(stageContext, selectedNodes, type);
};

function condition(context) {
  return context.api.getSelectedNodes().length > 0;
}

export const alignToPageTop = new Action({
  name: 'alignToPageTop',
  handler: createAlignHandler(ALIGN_PAGE_TYPES.top),
  condition: condition,
  icon: 'arrow-up',
});

export const alignToPageLeft = new Action({
  name: 'alignToPageLeft',
  handler: createAlignHandler(ALIGN_PAGE_TYPES.left),
  condition: condition,
  icon: 'arrow-left',
});

export const alignToPageMiddle = new Action({
  name: 'alignToPageMiddle',
  handler: createAlignHandler(ALIGN_PAGE_TYPES.middle),
  condition: condition,
  icon: 'align-center',
});

export const alignToPageCenter = new Action({
  name: 'alignToPageCenter',
  handler: createAlignHandler(ALIGN_PAGE_TYPES.center),
  condition: condition,
  icon: 'align-center',
});

export const alignToPageBottom = new Action({
  name: 'alignToPageBottom',
  handler: createAlignHandler(ALIGN_PAGE_TYPES.bottom),
  condition: condition,
  icon: 'arrow-down',
});

export const alignToPageRight = new Action({
  name: 'alignToPageRight',
  handler: createAlignHandler(ALIGN_PAGE_TYPES.right),
  condition: condition,
  icon: 'arrow-right',
});

export const alignToGroupTop = new Action({
  name: 'alignToTop',
  handler: createGroupLayerAlignHandler(ALIGN_PAGE_TYPES.top),
  condition: condition,
  icon: 'arrow-up',
  shortcuts: 'Alt+∑',
});

export const alignToGroupBottom = new Action({
  name: 'alignToBottom',
  handler: createGroupLayerAlignHandler(ALIGN_PAGE_TYPES.bottom),
  condition: condition,
  icon: 'arrow-down',
  shortcuts: 'Alt+SS',
});

export const alignToGroupCenter = new Action({
  name: 'alignToMiddle',
  handler: createGroupLayerAlignHandler(ALIGN_PAGE_TYPES.center),
  condition: condition,
  icon: 'arrow-center',
  shortcuts: 'Alt+˙',
});

export const alignToGroupMiddle = new Action({
  name: 'alignToCenter',
  handler: createGroupLayerAlignHandler(ALIGN_PAGE_TYPES.middle),
  condition: condition,
  icon: 'arrow-middle',
  shortcuts: 'Alt+√',
});

export const alignToGroupLeft = new Action({
  name: 'alignToLeft',
  handler: createGroupLayerAlignHandler(ALIGN_PAGE_TYPES.left),
  condition: condition,
  icon: 'arrow-left',
  shortcuts: 'Alt+Å',
});

export const alignToGroupRight = new Action({
  name: 'alignToRight',
  handler: createGroupLayerAlignHandler(ALIGN_PAGE_TYPES.right),
  condition: condition,
  icon: 'arrow-right',
  shortcuts: 'Alt+∂',
});

// Distribute/Spacing for multiple selected nodes
export const distributeHorizontalAction = new Action({
  name: 'DistributeHorizontal',
  handler: (stageContext) => {
    const selectedNodes = stageContext.api.getSelectedNodes();
    if (!selectedNodes || selectedNodes.length < 2) return;
    const updates = performDistribution(stageContext, selectedNodes, 'horizontal');
    applyAlignmentUpdates(stageContext, updates);
  },
});

export const distributeVerticalAction = new Action({
  name: 'DistributeVertical',
  handler: (stageContext) => {
    const selectedNodes = stageContext.api.getSelectedNodes();
    if (!selectedNodes || selectedNodes.length < 2) return;
    const updates = performDistribution(stageContext, selectedNodes, 'vertical');
    applyAlignmentUpdates(stageContext, updates);
  },
});

export const spaceHorizontalAction = new Action({
  name: 'EqualSpacingHorizontal',
  handler: (stageContext) => {
    const selectedNodes = stageContext.api.getSelectedNodes();
    if (!selectedNodes || selectedNodes.length < 2) return;
    const updates = performSpacing(stageContext, selectedNodes, 'horizontal');
    if (updates) applyAlignmentUpdates(stageContext, updates);
  },
});

export const spaceVerticalAction = new Action({
  name: 'EqualSpacingVertical',
  handler: (stageContext) => {
    const selectedNodes = stageContext.api.getSelectedNodes();
    if (!selectedNodes || selectedNodes.length < 2) return;
    const updates = performSpacing(stageContext, selectedNodes, 'vertical');
    if (updates) applyAlignmentUpdates(stageContext, updates);
  },
});

export function alignNodeGroup(stageContext, nodes, type) {
  if (!nodes || nodes.length < 2) return;

  // Get bounding rect of all selected nodes
  let minX = Infinity,
    minY = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity;

  nodes.forEach((node) => {
    minX = Math.min(minX, node.x);
    minY = Math.min(minY, node.y);
    maxX = Math.max(maxX, node.x + node.w);
    maxY = Math.max(maxY, node.y + node.h);
  });

  const groupWidth = maxX - minX;
  const groupHeight = maxY - minY;

  nodes.forEach((node) => {
    let x = node.x;
    let y = node.y;

    switch (type) {
      case ALIGN_PAGE_TYPES.top:
        y = minY;
        break;
      case ALIGN_PAGE_TYPES.left:
        x = minX;
        break;
      case ALIGN_PAGE_TYPES.middle:
        y = minY + (groupHeight - node.h) / 2;
        break;
      case ALIGN_PAGE_TYPES.center:
        x = minX + (groupWidth - node.w) / 2;
        break;
      case ALIGN_PAGE_TYPES.bottom:
        y = maxY - node.h;
        break;
      case ALIGN_PAGE_TYPES.right:
        x = maxX - node.w;
        break;
      default:
        return;
    }

    stageContext.api.updateNode(node.id, {
      x: x,
      y: y,
    });
  });
}

export function alignPageNode(stageContext, id, type) {
  const node = stageContext.api.getNode(id);
  if (!node) return;

  const ancenstors = stageContext.api.getAncenstors(node.id);
  const frame = ancenstors[0];

  const pageWidth = frame.w;
  const pageHeight = frame.h;

  let x, y;

  switch (type) {
    case ALIGN_PAGE_TYPES.top:
      x = node.x;
      y = 0;
      break;
    case ALIGN_PAGE_TYPES.left:
      x = 0;
      y = node.y;
      break;
    case ALIGN_PAGE_TYPES.middle:
      x = node.x;
      y = (pageHeight - node.h) / 2;
      break;
    case ALIGN_PAGE_TYPES.center:
      x = (pageWidth - node.w) / 2;
      y = node.y;
      break;
    case ALIGN_PAGE_TYPES.bottom:
      x = node.x;
      y = pageHeight - node.h;
      break;
    case ALIGN_PAGE_TYPES.right:
      x = pageWidth - node.w;
      y = node.y;
      break;
    default:
      return;
  }

  stageContext.api.updateNode(node.id, {
    x: x,
    y: y,
  });
}
